import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "@grudder/redux/store";
import { AUTH_GET, AUTH_PATCH, AUTH_POST, AUTH_PUT } from "@grudder/app/actions-client";
import { config } from "@grudder/lib/config";
import { CreateWorkflowFormData, Workflow, RoundRobinAssignmentConfig, UpdateFieldConfig, WorkflowAction, WorkflowActionType, WebhookConfig, CapiConfig } from "@grudder/types/workflow/workflow.types";
import { LEAD_FILTER_INPUT_FIELDS } from "@grudder/utils/lead-advanced-filter-utils";
import { queryClient } from "@grudder/providers/QueryClientProviders";
import { APIAuthenticationTypeEnum, APICallActionAttributes } from "@grudder/types/workflow/workflow-api-input.types";



// Minimal mappers aligned with hooks implementation
const mapBackendTriggerToUi = (trigger?: string) => {
	if (trigger === "CREATE")
		return {
			id: "lead_created",
			name: "Lead Created",
			type: "lead_created",
			description: "Triggered when a new lead is created",
		} as any;
	if (trigger === "UPDATE")
		return {
			id: "lead_updated",
			name: "Lead Updated",
			type: "lead_updated",
			description: "Triggered when a lead is updated",
		} as any;
	return {
		id: "lead_created",
		name: "Lead Created",
		type: "lead_created",
		description: "Triggered when a new lead is created",
	} as any;
};

const mapBackendOperatorToUi = (op: string): string => {
	const m: Record<string, string> = {
		EQUALS: "equals",
		NOT_EQUALS: "not_equals",
		CONTAINS: "contains",
		NOT_CONTAINS: "not_contains",
		IN: "in",
		NOT_IN: "not_in",
		GREATER_THAN: "greater_than",
		LESS_THAN: "less_than",
		BETWEEN: "between",
		IS_NULL: "is_null",
		IS_NOT_NULL: "is_not_null",
		IS_EMPTY: "is_empty",
		IS_NOT_EMPTY: "is_not_empty",
		NOT: "not",
	};
	return m[op] || "equals";
};

const resolveFieldType = (fieldKey: string): UpdateFieldConfig["fieldType"] => {
	return LEAD_FILTER_INPUT_FIELDS.some((f: any) => f.key === fieldKey) ? "standard" : "custom";
};

const mapBackendUpdateFieldsToUi = (fields: any): UpdateFieldConfig[] => {
	if (!fields) return [];
	if (Array.isArray(fields)) {
		return fields
			.map((field: any, index: number) => {
				const key = field?.field || field?.key || field?.mapTo;
				if (!key) return null;
				const valueType = typeof field?.valueType === "string" ? field.valueType.toLowerCase() : "fixed";
				return {
					id: field.id || `${key}_${index}_${Date.now()}`,
					field: key,
					fieldType: (field?.fieldType as UpdateFieldConfig["fieldType"]) || resolveFieldType(key),
					valueType: (valueType as UpdateFieldConfig["valueType"]) || "fixed",
					value: field?.value ?? "",
					referenceField: field?.referenceField,
					formula: field?.formula,
				} as UpdateFieldConfig;
			})
			.filter(Boolean) as UpdateFieldConfig[];
	}
	return Object.entries(fields).map(([key, value]: [string, any], index) => {
		const valueType = typeof value?.valueType === "string" ? value.valueType.toLowerCase() : "fixed";
		const resolvedValue = typeof value === "object" && value !== null ? value?.value : value;
		return {
			id: value?.id || `${key}_${index}_${Date.now()}`,
			field: key,
			fieldType: (value?.fieldType as UpdateFieldConfig["fieldType"]) || resolveFieldType(key),
			valueType: (valueType as UpdateFieldConfig["valueType"]) || "fixed",
			value: resolvedValue ?? "",
			referenceField: value?.referenceField,
			formula: value?.formula,
		} as UpdateFieldConfig;
	});
};

const mapSingleBackendActionToUi = (action: any, index: number): WorkflowAction | null => {
	if (!action) return null;
	const { actionType, attributes } = action;
	let type: WorkflowAction["type"] = WorkflowActionType.UPDATE_LEAD;
	let config: WorkflowAction["config"] = {};
	if (actionType === "ROUND_ROBIN_FOR_LEAD_ASSIGNMENT") {
		type = WorkflowActionType.ROUND_ROBIN_LEAD_ASSIGNMENT;
		const settings = attributes?.roundRobinSettings || {};
		const users = Array.isArray(settings?.users) ? settings.users.filter(Boolean) : [];
		const groupId = settings?.groupId || undefined;
		config = {
			assignmentMode: groupId ? "group" : "users",
			userIds: users,
			groupId,
			includeManagers: settings?.isManagerAssign ?? false,
			priority: settings?.priority || "MEDIUM",
			description: settings?.description || "",
		} as RoundRobinAssignmentConfig;
	}
	else if (actionType === "EXTERNAL_API_CALL") {
		type = WorkflowActionType.TRIGGER_WEBHOOK;
		const apiSettings: APICallActionAttributes = attributes?.apiCallActionAttributes || {};
		const headersArray = apiSettings?.additionalHeaders
			? Object.entries(apiSettings.additionalHeaders).map(([key, value]) => ({ key, value }))
			: [];

		const webhookConfig: WebhookConfig = {
			method: apiSettings?.method || "GET",
			url: apiSettings?.url || "",
			authentication: {
				enabled: !!apiSettings?.authentication,
				type: apiSettings?.authentication?.type || APIAuthenticationTypeEnum.BASIC,
				username: apiSettings?.authentication?.username || "",
				password: apiSettings?.authentication?.password || "",
				apiKey: apiSettings?.authentication?.api_key_value || "",
				apiKeyHeader: apiSettings?.authentication?.api_key_name || "",
			},
			headers: headersArray,
			encoding: apiSettings?.encoding || "json",
			body: apiSettings?.body as any || {},
			// timeout: apiSettings?.timeout || 10000,
		}


		config = webhookConfig;
	}
	else if (actionType === "CAPI_INTEGRATION") {
		type = WorkflowActionType.CAPI_INTEGRATION;
		const capiSettings = attributes?.capiActionAttributes || {};
		config = {
			account: capiSettings?.account || "",
			eventName: capiSettings?.eventName || "Lead",
			testEventCode: capiSettings?.testEventCode || "",
		} as CapiConfig;
	}
	else {
		type = WorkflowActionType.UPDATE_LEAD;
		const updateAttributes = attributes?.updateActionAttributes || {};
		config = {
			updateFields: mapBackendUpdateFieldsToUi(updateAttributes?.fields),
		};
	}
	return {
		id: action.id || `${Date.now()}_${index}`,
		type: type || WorkflowActionType.UPDATE_LEAD,
		name: type === WorkflowActionType.CAPI_INTEGRATION ? "CAPI Integration" : type === WorkflowActionType.UPDATE_LEAD ? "Update Lead" : "Round Robin Lead Assignment",
		description: "",
		config,
	} as WorkflowAction;
};

const mapBackendActionsToUi = (actions: any): WorkflowAction[] => {
	if (!actions) return [];
	const list = Array.isArray(actions) ? actions : [actions];
	return list.map((action, index) => mapSingleBackendActionToUi(action, index)).filter(Boolean) as WorkflowAction[];
};

export const fetchWorkflows = createAsyncThunk<Workflow[], void, { state: RootState }>(
	"workflows/fetchAll",
	async (_, { rejectWithValue }) => {
		try {
			// Fetch all pages to avoid default API limit (often 10)
			const PAGE_SIZE = 100;
			let skip = 0;
			let all: any[] = [];
			// Safety cap to avoid infinite loops
			for (let i = 0; i < 50; i++) {
				const res: any = await AUTH_GET(`${config.baseURL}/api/automations?limit=${PAGE_SIZE}&skip=${skip}`, { tags: ["automations"] });
				const items = Array.isArray(res?.data) ? res.data : [];
				all = all.concat(items);
				if (items.length < PAGE_SIZE) break;
				skip += PAGE_SIZE;
			}
			const mapped: Workflow[] = all.map((a: any) => ({
				id: a.id || a._id || `${Date.now()}`,
				name: a.name || "",
				description: a.description || "",
				module: "lead",
				trigger: mapBackendTriggerToUi(a.trigger),
				conditions: (a.conditions || []).map((c: any) => ({
					id: c.id || c._id || `${Date.now()}_${Math.random()}`,
					field: c.modelField,
					fieldType: LEAD_FILTER_INPUT_FIELDS.some((f: any) => f.key === c.modelField) ? "standard" : "custom",
					operator: mapBackendOperatorToUi(c.trigger),
					value: c.rhsValue,
				})),
				conditionLogic: (a.rootConditionType || "AND").toUpperCase() === "OR" ? "OR" : "AND",
				actions: mapBackendActionsToUi(a.action),
				isActive: a.status === "ACTIVE",
				createdAt: a.createdAt,
				updatedAt: a.updatedAt,
				createdBy: a.createdBy || "system",
			}));
			return mapped;
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to fetch workflows");
		}
	}
);

// Force refresh workflows (ignores cache)
export const forceRefreshWorkflows = createAsyncThunk<Workflow[], void, { state: RootState }>(
	"workflows/forceRefresh",
	async (_, { rejectWithValue }) => {
		try {
			const PAGE_SIZE = 100;
			let skip = 0;
			let all: any[] = [];
			for (let i = 0; i < 50; i++) {
				const res: any = await AUTH_GET(`${config.baseURL}/api/automations?limit=${PAGE_SIZE}&skip=${skip}`, { tags: ["automations"], cache: 'no-store' });
				const items = Array.isArray(res?.data) ? res.data : [];
				all = all.concat(items);
				if (items.length < PAGE_SIZE) break;
				skip += PAGE_SIZE;
			}
			const mapped: Workflow[] = all.map((a: any) => ({
				id: a.id || a._id || `${Date.now()}`,
				name: a.name || "",
				description: a.description || "",
				module: "lead",
				trigger: mapBackendTriggerToUi(a.trigger),
				conditions: (a.conditions || []).map((c: any) => ({
					id: c.id || c._id || `${Date.now()}_${Math.random()}`,
					field: c.modelField,
					fieldType: LEAD_FILTER_INPUT_FIELDS.some((f: any) => f.key === c.modelField) ? "standard" : "custom",
					operator: mapBackendOperatorToUi(c.trigger),
					value: c.rhsValue,
				})),
				conditionLogic: (a.rootConditionType || "AND").toUpperCase() === "OR" ? "OR" : "AND",
				actions: mapBackendActionsToUi(a.action),
				isActive: a.status === "ACTIVE",
				createdAt: a.createdAt,
				updatedAt: a.updatedAt,
				createdBy: a.createdBy || "system",
			}));
			return mapped;
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to fetch workflows");
		}
	}
);

export const toggleWorkflowStatus = createAsyncThunk<{ id: string; isActive: boolean }, { id: string; isActive: boolean }>(
	"workflows/toggleStatus",
	async ({ id, isActive }, { rejectWithValue }) => {
		try {
			const status = isActive ? "ACTIVE" : "PAUSED";
			await AUTH_PATCH(`${config.baseURL}/api/automations/${id}`, { status }, { tags: ["automations", `automation:${id}`] });
			return { id, isActive };
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to toggle workflow");
		}
	}
);

export const pauseWorkflow = createAsyncThunk<{ id: string; isActive: boolean }, { id: string }>(
	"workflows/pause",
	async ({ id }, { rejectWithValue }) => {
		try {
			await AUTH_PATCH(`${config.baseURL}/api/automations/${id}`, { status: "PAUSED" }, { tags: ["automations", `automation:${id}`] });
			return { id, isActive: false };
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to pause workflow");
		}
	}
);

export const resumeWorkflow = createAsyncThunk<{ id: string; isActive: boolean }, { id: string }>(
	"workflows/resume",
	async ({ id }, { rejectWithValue }) => {
		try {
			await AUTH_PATCH(`${config.baseURL}/api/automations/${id}`, { status: "ACTIVE" }, { tags: ["automations", `automation:${id}`] });
			return { id, isActive: true };
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to resume workflow");
		}
	}
);

export const deleteWorkflowById = createAsyncThunk<string, string>(
	"workflows/delete",
	async (id, { rejectWithValue }) => {
		try {
			await AUTH_PATCH(`${config.baseURL}/api/automations/${id}`, { status: "DELETED" }, { tags: ["automations", `automation:${id}`] });
			return id;
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to delete workflow");
		}
	}
);

// Helpers to build backend payloads from UI form
const mapUiOperatorToBackend = (op: string): string => {
	const m: Record<string, string> = {
		not: "NOT",
		equals: "EQUALS",
		not_equals: "NOT_EQUALS",
		contains: "CONTAINS",
		not_contains: "NOT_CONTAINS",
		in: "IN",
		not_in: "NOT_IN",
		greater_than: "GREATER_THAN",
		less_than: "LESS_THAN",
		between: "BETWEEN",
		is_null: "IS_NULL",
		is_not_null: "IS_NOT_NULL",
		is_empty: "IS_EMPTY",
		is_not_empty: "IS_NOT_EMPTY",
	};
	return m[op] || "EQUALS";
};

const mapTriggerToBackend = (triggerType?: string): string => {
	if (triggerType === "lead_created") return "CREATE";
	if (triggerType === "lead_updated" || triggerType === "lead_repeat") return "UPDATE";
	return "CREATE";
};

const mapUiActionToBackend = (ui: WorkflowAction | any) => {
	console.log("ui", ui);
	if (!ui) return null;
	if (ui.type === WorkflowActionType.ROUND_ROBIN_LEAD_ASSIGNMENT) {
		const settings: RoundRobinAssignmentConfig = ui.config || {};
		const assignmentMode = settings?.assignmentMode || "users";
		const users = Array.isArray(settings?.userIds) ? settings.userIds.filter(Boolean) : [];
		const priority = (settings?.priority || "MEDIUM").toUpperCase();
		const allowedPriorities = ["LOW", "MEDIUM", "HIGH"];
		const roundRobinSettings: any = {
			users: assignmentMode === "users" ? users : [],
			isManagerAssign: !!settings?.includeManagers,
			description: settings?.description || "",
			priority: allowedPriorities.includes(priority) ? priority : "MEDIUM",
		};
		if (assignmentMode === "group" && settings?.groupId) {
			roundRobinSettings.groupId = settings.groupId;
		}
		return {
			actionType: "ROUND_ROBIN_FOR_LEAD_ASSIGNMENT",
			module: "LEAD",
			attributes: { roundRobinSettings },
		};
	}

	if (ui.type === WorkflowActionType.TRIGGER_WEBHOOK) {

		const input = ui;
		const settings: APICallActionAttributes = {
			method: input.config?.method as any,
			url: input.config?.url || "",
			authentication: {
				type: input.config?.authentication?.type || APIAuthenticationTypeEnum.BASIC,
				username: input.config?.authentication?.username || "",
				password: input.config?.authentication?.password || "",
				api_key_value: input.config?.authentication?.apiKey || "",
				api_key_name: input.config?.authentication?.apiKeyHeader || "",
				token: input.config?.authentication?.token || "",
			},
			additionalHeaders: input.config?.headers?.reduce((acc: any, header: any) => {
				acc[header.key] = header.value;
				return acc;
			}, {} as Record<string, string>) || {},
			encoding: input.config?.encoding || "json" as any,
			body: input.config?.body || {},
			// timeout: input.config?.timeout || 10000,
		};




		return {
			actionType: "EXTERNAL_API_CALL",
			module: "LEAD",
			attributes: { apiCallActionAttributes: settings },
		};
	}

	if (ui.type === WorkflowActionType.CAPI_INTEGRATION) {
		const capiConfig: Partial<CapiConfig> = ui.config || {};
		return {
			actionType: "CAPI_INTEGRATION",
			module: "LEAD",
			attributes: {
				capiActionAttributes: {
					account: capiConfig?.account || "",
					eventName: capiConfig?.eventName || "Lead",
					testEventCode: capiConfig?.testEventCode || undefined,
				},
			},
		};
	}

	const updateConfig = ui.config || {};
	const updateFields: UpdateFieldConfig[] = Array.isArray(updateConfig.updateFields) ? updateConfig.updateFields : [];
	const fieldsPayload: Record<string, any> = {};
	const customDataTypesPayload: Array<{ cdt: string; value?: any; referenceField?: string }> = [];

	updateFields.forEach((field) => {
		if (!field?.field) return;

		if (field.fieldType === "custom") {
			if (field.valueType === "reference" && field.referenceField) {
				customDataTypesPayload.push({ cdt: field.field, referenceField: field.referenceField });
			} else if (field.valueType === "fixed") {
				customDataTypesPayload.push({ cdt: field.field, value: field?.value });
			}
			return;
		}

		fieldsPayload[field.field] = field?.value;
	});

	if (customDataTypesPayload.length > 0) {
		fieldsPayload.customDataTypes = customDataTypesPayload;
	}

	return {
		actionType: "UPDATE",
		module: "LEAD",
		attributes: {
			updateActionAttributes: {
				filter: [],
				fields: fieldsPayload,
				description: "",
				priority: "MEDIUM",
			},
		},
	};
};

const mapUiActionsToBackend = (uiActions: WorkflowAction[] | any[]): any[] => {
	if (!Array.isArray(uiActions)) return [];
	return uiActions
		.map((action) => mapUiActionToBackend(action))
		.filter(Boolean);
};

const getValueTypeForField = (fieldKey: string): string => {
	const f = LEAD_FILTER_INPUT_FIELDS.find((ff: any) => ff.key === fieldKey);
	if (!f) return "STRING";
	return f.type || "STRING";
};

const buildAutomationPayload = (formData: CreateWorkflowFormData) => {
	const conditions = (formData.conditions || []).map((c: any) => ({
		trigger: mapUiOperatorToBackend(c.operator),
		modelField: c.field,
		valueType: getValueTypeForField(c.field),
		rhsValue: c.value,
	}));
	const hasMultiple = conditions.length > 1;
	const backendActions = mapUiActionsToBackend(formData.actions || []);
	const fallbackAction = mapUiActionToBackend({ type: WorkflowActionType.UPDATE_LEAD, config: { updateFields: [] } });
	const resolvedActions = backendActions.length > 0 ? backendActions : fallbackAction ? [fallbackAction] : [];
	const payload: any = {
		name: formData.name,
		description: formData.description,
		module: "LEAD",
		triggerOrder: "AFTER",
		trigger: mapTriggerToBackend(formData.trigger?.type),
		...(hasMultiple ? { rootConditionType: (formData.conditionLogic || "AND").toUpperCase() } : {}),
		conditions,
		action: resolvedActions,
		status: "ACTIVE",
	};
	return payload;
};

// Create automation
export const createWorkflow = createAsyncThunk<any, CreateWorkflowFormData>(
	"workflows/create",
	async (formData, { rejectWithValue }) => {
		try {
			const payload = buildAutomationPayload(formData);
			const res = await AUTH_POST(`${config.baseURL}/api/automations`, payload, { tags: ["automations"] });
			return res;
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to create workflow");
		}
	}
);

// Update automation
export const updateWorkflow = createAsyncThunk<any, { id: string; data: CreateWorkflowFormData }>(
	"workflows/update",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const payload = buildAutomationPayload(data);
			const res = await AUTH_PUT(`${config.baseURL}/api/automations/${id}`, payload, { tags: ["automations", `automation:${id}`] });
			await queryClient.invalidateQueries({
				predicate: (query) => query.queryKey?.some((key) => key === "automations" || key === `automation:${id}`),
			});
			return res;
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to update workflow");
		}
	}
);

// Get single automation by id
export const getWorkflowById = createAsyncThunk<any, string>(
	"workflows/getById",
	async (id, { rejectWithValue }) => {
		try {
			const res = await AUTH_GET(`${config.baseURL}/api/automations/${id}`, { tags: ["automations", `automation:${id}`], cache: "no-store" });
			return res;
		} catch (error: any) {
			return rejectWithValue(error?.message || "Failed to fetch workflow details");
		}
	}
);

